// src/Components/Dashboard/TeacherDashboard/ManagePlacementResults.jsx
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ReplayIcon from "@mui/icons-material/Replay";
import SecurityIcon from "@mui/icons-material/Security";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import SendIcon from "@mui/icons-material/Send";
import {
  Avatar,
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Pagination,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  enableRetake,
  fetchPlacementResults,
  sendPlacementMessage,
} from "../../../redux/Actions/teacherPlacementActions";

import MessageCard from "../../MessageCard/MessageCard";
import "./CvManagementPage.css";
import TeacherNavbar from "./TeacherNavbar";
import PlacementTestAnalytics from "../Admin-panel/PlacementTestAnalytics";
import api from "lib/api/api";

function pickMessage(row, lang = "en") {
  if (lang === "ar") return row.studentMessageAr || row.studentMessage || row.studentMessageEn || "";
  if (lang === "de") return row.studentMessageDe || row.studentMessage || row.studentMessageEn || "";
  return row.studentMessageEn || row.studentMessage || row.studentMessageDe || "";
}

function ResultSummary({ row }) {
  const score = Number(row.normalizedScore ?? row.scores?.total ?? 0);
  return (
    <Stack spacing={1.25}>
      <Stack direction="row" spacing={1} flexWrap="wrap">
        <Chip label={`Score ${score}/100`} color="warning" size="small" sx={{ fontWeight: 800 }} />
        {row.targetLevel && <Chip label={`Target ${row.targetLevel}`} size="small" variant="outlined" />}
        {row.finalLevel && <Chip label={`Final ${row.finalLevel}`} size="small" color="success" variant="outlined" />}
        {row.resultStatus && <Chip label={row.resultStatus} size="small" />}
        {row.allowBooking !== null && row.allowBooking !== undefined && (
          <Chip
            label={row.allowBooking ? "Booking allowed" : "Booking blocked / review first"}
            color={row.allowBooking ? "success" : "warning"}
            size="small"
            variant="outlined"
          />
        )}
      </Stack>
      <LinearProgress
        variant="determinate"
        value={Math.max(0, Math.min(100, score))}
        sx={{
          height: 8,
          borderRadius: 99,
          bgcolor: "rgba(17, 24, 39, 0.08)",
          "& .MuiLinearProgress-bar": { bgcolor: "#ffc107" },
        }}
      />
      <Typography variant="body2" color="text.secondary">
        Raw MCQ score: {row.spScore ?? 0}. Schreiben is ignored for the new placement test.
      </Typography>
    </Stack>
  );
}

export default function ManagePlacementResults() {
  const dispatch = useDispatch();
  const { rows, loading, totalPages, enablingRetake, error } = useSelector(
    (s) =>
      s.teacherPlacement || {
        rows: [],
        loading: false,
        totalPages: 0,
        enablingRetake: {},
      }
  );

  const [uiPage, setUiPage] = useState(1);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") === "analytics" ? 2 : 0);
  const [q, setQ] = useState("");
  const [studentTypeFilter, setStudentTypeFilter] = useState("all");
  const [expanded, setExpanded] = useState({});
  const [msg, setMsg] = useState(null);
  const [msgType, setMsgType] = useState("success");
  const [confirmRetake, setConfirmRetake] = useState({ open: false, row: null });
  const [messageDialog, setMessageDialog] = useState({
    open: false,
    row: null,
    subject: "Placement Test Result",
    message: "",
  });
  const [securityRows, setSecurityRows] = useState([]);
  const [securityLoading, setSecurityLoading] = useState(false);
  const [blacklistDialog, setBlacklistDialog] = useState({
    open: false,
    row: null,
    reason: "Manual teacher decision",
  });
  const roleNames = useSelector((state) => state.auth.user.roleNames || []);
  const isAdmin = roleNames.some((role) => ["ADMIN", "ADMIN_DEVELOPER"].includes(String(role).toUpperCase()));
  const [managerPermissions, setManagerPermissions] = useState({});
  const [reportDialog, setReportDialog] = useState({ open: false, loading: false, row: null, data: null, error: "" });
  const [reportAction, setReportAction] = useState("");
  const mayViewReports = isAdmin || !!managerPermissions.viewPlacementDetailedReports;
  const maySendNormal = isAdmin || !!managerPermissions.sendPlacementResultEmails;
  const maySendDetailed = isAdmin || !!managerPermissions.sendPlacementDetailedReports;
  const mayViewAnalytics = isAdmin || !!managerPermissions.viewPlacementAnalytics;

  useEffect(() => {
    api.get("/teacher-manager/permissions")
      .then((response) => setManagerPermissions(response.data || {}))
      .catch(() => setManagerPermissions({}));
  }, []);

  useEffect(() => {
    dispatch(fetchPlacementResults(uiPage - 1, 20));
  }, [dispatch, uiPage]);

  const loadSecurityOverview = async () => {
    try {
      setSecurityLoading(true);
      const { data } = await api.get("/placement/monitor/admin/security-overview");
      setSecurityRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setMsg(err?.response?.data?.message || err.message || "Failed to load security overview");
      setMsgType("error");
    } finally {
      setSecurityLoading(false);
    }
  };

  useEffect(() => {
    loadSecurityOverview();
  }, []);

  const filteredRows = useMemo(() => {
    let next = rows;
    if (studentTypeFilter === "guest") {
      next = next.filter((r) => !!r.guest);
    } else if (studentTypeFilter === "account") {
      next = next.filter((r) => !r.guest);
    }

    next = [...next].sort((a, b) => {
      if (Number(a.guest) !== Number(b.guest)) {
        return Number(b.guest) - Number(a.guest);
      }
      return new Date(b.submittedAt || 0) - new Date(a.submittedAt || 0);
    });

    if (!q.trim()) return next;
    const term = q.trim().toLowerCase();
    return next.filter(
      (r) =>
        r.studentName?.toLowerCase().includes(term) ||
        r.email?.toLowerCase().includes(term) ||
        r.targetLevel?.toLowerCase().includes(term)
    );
  }, [rows, q, studentTypeFilter]);

  const toggleExpand = (id) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const onEnableRetake = async (row) => {
    const res = await dispatch(enableRetake(row.sessionId, row.studentId));
    if (res?.ok) {
      setMsg(res.message || "Retake enabled");
      setMsgType("success");
      dispatch(fetchPlacementResults(uiPage - 1, 20));
    } else {
      setMsg(res?.message || "Enable retake failed");
      setMsgType("error");
    }
  };

  const handleOpenRetake = (row) => setConfirmRetake({ open: true, row });
  const handleCancelRetake = () => setConfirmRetake({ open: false, row: null });
  const handleConfirmRetake = async () => {
    const row = confirmRetake.row;
    handleCancelRetake();
    if (row) await onEnableRetake(row);
  };

  const openMessageDialog = (row) => {
    setMessageDialog({
      open: true,
      row,
      subject: "Placement Test Result",
      message: pickMessage(row, "en") || `Your placement result is ${row.normalizedScore ?? "-"} points.`,
    });
  };

  const closeMessageDialog = () =>
    setMessageDialog((prev) => ({ ...prev, open: false, row: null }));

  const submitMessage = async () => {
    const row = messageDialog.row;
    if (!row) return;
    const res = await dispatch(
      sendPlacementMessage({
        sessionId: row.sessionId,
        studentId: row.studentId,
        subject: messageDialog.subject,
        message: messageDialog.message,
      })
    );
    if (res?.ok) {
      setMsg(res.message || "Message sent");
      setMsgType("success");
      closeMessageDialog();
    } else {
      setMsg(res?.message || "Message failed");
      setMsgType("error");
    }
  };

  const blacklistStudent = async () => {
    const row = blacklistDialog.row;
    if (!row) return;
    try {
      const { data } = await api.post("/placement/monitor/admin/blacklist", {
        studentId: row.studentId,
        reason: blacklistDialog.reason,
      });
      setMsg(data?.message || "Student blacklisted");
      setMsgType("success");
      setBlacklistDialog({ open: false, row: null, reason: "Manual teacher decision" });
      loadSecurityOverview();
    } catch (err) {
      setMsg(err?.response?.data?.message || err.message || "Blacklist failed");
      setMsgType("error");
    }
  };

  const removeBlacklist = async (row) => {
    try {
      const { data } = await api.delete(`/placement/monitor/admin/blacklist/${row.studentId}`);
      setMsg(data?.message || "Student removed from blacklist");
      setMsgType("success");
      loadSecurityOverview();
    } catch (err) {
      setMsg(err?.response?.data?.message || err.message || "Remove blacklist failed");
      setMsgType("error");
    }
  };

  const resetWarnings = async (row) => {
    try {
      const { data } = await api.post(`/placement/monitor/admin/reset-warnings/${row.studentId}`);
      setMsg(data?.message || "Warnings reset");
      setMsgType("success");
      loadSecurityOverview();
    } catch (err) {
      setMsg(err?.response?.data?.message || err.message || "Reset warnings failed");
      setMsgType("error");
    }
  };

  const previewDetailedReport = async (row) => {
    setReportDialog({ open: true, loading: true, row, data: null, error: "" });
    try {
      const { data } = await api.get(`/teacher-manager/placement-test/reports/${row.sessionId}/preview`);
      setReportDialog({ open: true, loading: false, row, data, error: "" });
    } catch (err) {
      setReportDialog({ open: true, loading: false, row, data: null, error: err.response?.data?.message || "Could not build the detailed report." });
    }
  };

  const downloadDetailedReport = async (row) => {
    try {
      setReportAction(`pdf-${row.sessionId}`);
      const response = await api.get(`/teacher-manager/placement-test/reports/${row.sessionId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `placement-report-session-${row.sessionId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setMsg(err.response?.data?.message || "Could not download the detailed report.");
      setMsgType("error");
    } finally { setReportAction(""); }
  };

  const sendReportEmail = async (row, detailed) => {
    try {
      setReportAction(`${detailed ? "detailed" : "normal"}-${row.sessionId}`);
      const endpoint = detailed
        ? `/teacher-manager/placement-test/reports/${row.sessionId}/email`
        : `/teacher-manager/placement-test/sessions/${row.sessionId}/result-email`;
      const { data } = await api.post(endpoint);
      setMsg(data?.message || "Email sent.");
      setMsgType("success");
    } catch (err) {
      setMsg(err.response?.data?.message || "Email delivery failed.");
      setMsgType("error");
    } finally { setReportAction(""); }
  };

  return (
    <div className="cv-management-layout">
      <TeacherNavbar />

      <Box className="cv-management-container">
        <Typography variant="h4" gutterBottom className="cv-management-header">
          Manage Placement Results
        </Typography>

        {msg && <MessageCard message={msg} type={msgType} onClose={() => setMsg(null)} />}

        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          sx={{ mb: 2, borderBottom: "1px solid #e5e7eb" }}
        >
          <Tab label="Results" />
          <Tab label="Manage cheating & blacklist" />
          {mayViewAnalytics && <Tab label="Analytics" />}
        </Tabs>

        {activeTab === 2 && mayViewAnalytics && (
          <PlacementTestAnalytics endpoint="/teacher-manager/placement-test/analytics" embedded />
        )}

        {activeTab === 0 && (
        <Stack direction="row" spacing={2} alignItems="center" mb={2}>
          <TextField
            label="Search by name, email, or target level"
            size="small"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            sx={{ width: 360 }}
          />
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant={studentTypeFilter === "all" ? "contained" : "outlined"}
              onClick={() => setStudentTypeFilter("all")}
            >
              All
            </Button>
            <Button
              size="small"
              variant={studentTypeFilter === "account" ? "contained" : "outlined"}
              onClick={() => setStudentTypeFilter("account")}
            >
              Account students
            </Button>
            <Button
              size="small"
              variant={studentTypeFilter === "guest" ? "contained" : "outlined"}
              onClick={() => setStudentTypeFilter("guest")}
            >
              Guests
            </Button>
          </Stack>
        </Stack>
        )}

        {activeTab === 1 && (
        <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
          <CardHeader
            avatar={<SecurityIcon color="warning" />}
            title="Placement security / blacklist"
            subheader="Policy: warning 1, warning 2, warning 3, then blacklist on the next cheating attempt."
            action={
              <Button size="small" variant="outlined" onClick={loadSecurityOverview}>
                Refresh
              </Button>
            }
          />
          <CardContent>
            {securityLoading && <Typography>Loading security overview...</Typography>}
            {!securityLoading && securityRows.length === 0 && (
              <Typography color="text.secondary">No students found.</Typography>
            )}
            <Stack spacing={1.25}>
              {!securityLoading &&
                securityRows
                  .filter(
                    (row) =>
                      row.blacklisted ||
                      Number(row.warningCount || 0) > 0 ||
                      Number(row.placementAttemptCount || 0) > 0
                  )
                  .map((row) => (
                    <Box
                      key={row.studentId}
                      sx={{
                        p: 1.5,
                        border: "1px solid #e5e7eb",
                        borderRadius: 2,
                        bgcolor: row.blacklisted ? "#fff1f2" : "#fff8db",
                      }}
                    >
                      <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={1}
                        alignItems={{ xs: "flex-start", md: "center" }}
                        justifyContent="space-between"
                      >
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                            {row.studentName || "Student"} · {row.email || "-"}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Warnings: {row.warningCount || 0}/3 · Last status: {row.lastStatus || "-"}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Total placement test starts: {row.placementAttemptCount || 0}
                          </Typography>
                          {row.lastMessage && (
                            <Typography variant="caption" color="text.secondary">
                              {row.lastMessage}
                            </Typography>
                          )}
                          {row.blacklistReason && (
                            <Typography variant="caption" color="error" display="block">
                              Blacklist reason: {row.blacklistReason}
                            </Typography>
                          )}
                        </Box>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          {row.blacklisted ? (
                            <Button size="small" variant="outlined" onClick={() => removeBlacklist(row)}>
                              Remove blacklist
                            </Button>
                          ) : (
                            <Button
                              size="small"
                              color="error"
                              variant="outlined"
                              onClick={() =>
                                setBlacklistDialog({
                                  open: true,
                                  row,
                                  reason: "Manual teacher decision",
                                })
                              }
                            >
                              Blacklist
                            </Button>
                          )}
                          <Button size="small" variant="outlined" onClick={() => resetWarnings(row)}>
                            Reset warnings
                          </Button>
                        </Stack>
                      </Stack>
                    </Box>
                  ))}
            </Stack>
            {!securityLoading &&
              securityRows.filter(
                (row) =>
                  row.blacklisted ||
                  Number(row.warningCount || 0) > 0 ||
                  Number(row.placementAttemptCount || 0) > 0
              ).length === 0 && (
                <Typography color="text.secondary">
                  No placement attempts, warnings, or blacklisted students.
                </Typography>
              )}
          </CardContent>
        </Card>
        )}

        {activeTab === 0 && (
        <Stack spacing={2}>
          {loading && <Typography>Loading...</Typography>}
          {error && <Typography color="error">{error}</Typography>}
          {!loading && filteredRows.length === 0 && (
            <Typography color="text.secondary">No results</Typography>
          )}

          {!loading &&
            filteredRows.map((row) => {
              const key = row.sessionId ?? row.id;
              const isFinal = row.status === "FINAL";
              return (
                <Card key={row.id || key} variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardHeader
                    avatar={
                      <Avatar src={row.avatarUrl || undefined}>
                        {row.studentName?.[0] || "S"}
                      </Avatar>
                    }
                    title={
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Typography variant="subtitle1" fontWeight={800}>
                          {row.studentName || "Student"}
                        </Typography>
                        <Chip
                          label={row.guest ? "Guest" : "Account"}
                          color={row.guest ? "warning" : "primary"}
                          size="small"
                          variant="outlined"
                        />
                        {row.blacklisted && <Chip label="Blacklisted" color="error" size="small" />}
                        {row.status && (
                          <Chip label={row.status} size="small" color={isFinal ? "success" : "default"} />
                        )}
                      </Stack>
                    }
                    subheader={
                      <Typography variant="body2" color="text.secondary">
                        {row.email} · Submitted{" "}
                        {row.submittedAt ? new Date(row.submittedAt).toLocaleString() : "-"}
                      </Typography>
                    }
                  />

                  <CardContent>
                    <ResultSummary row={row} />
                  </CardContent>

                  <CardActions sx={{ justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<MailOutlineIcon />}
                        onClick={() => openMessageDialog(row)}
                      >
                        Message Student
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        color="warning"
                        startIcon={<ReplayIcon />}
                        onClick={() => handleOpenRetake(row)}
                        disabled={!!enablingRetake[key]}
                      >
                        {enablingRetake[key] ? "Enabling..." : "Enable Retake"}
                      </Button>
                      {mayViewReports && (
                        <Button size="small" variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={() => previewDetailedReport(row)}>
                          Preview report
                        </Button>
                      )}
                      {maySendNormal && (
                        <Button size="small" variant="outlined" startIcon={<MailOutlineIcon />} onClick={() => sendReportEmail(row, false)} disabled={!!reportAction}>
                          {reportAction === `normal-${row.sessionId}` ? "Sending..." : "Send result"}
                        </Button>
                      )}
                      {maySendDetailed && (
                        <Button size="small" variant="contained" startIcon={<SendIcon />} onClick={() => sendReportEmail(row, true)} disabled={!!reportAction}>
                          {reportAction === `detailed-${row.sessionId}` ? "Sending..." : "Email detailed PDF"}
                        </Button>
                      )}
                    </Stack>

                    <Tooltip title="Show result messages">
                      <IconButton onClick={() => toggleExpand(key)}>
                        <ExpandMoreIcon
                          sx={{
                            transform: expanded[key] ? "rotate(180deg)" : "rotate(0deg)",
                            transition: "0.2s",
                          }}
                        />
                      </IconButton>
                    </Tooltip>
                  </CardActions>

                  <Collapse in={!!expanded[key]} timeout="auto" unmountOnExit>
                    <CardContent>
                      <Stack spacing={1.5}>
                        <Typography variant="subtitle2">Student result messages</Typography>
                        <Divider />
                        <Box sx={{ p: 1.5, bgcolor: "#fafafa", borderRadius: 1 }}>
                          <Typography variant="caption" color="text.secondary">English</Typography>
                          <Typography variant="body2">{pickMessage(row, "en") || "-"}</Typography>
                        </Box>
                        <Box sx={{ p: 1.5, bgcolor: "#fafafa", borderRadius: 1 }}>
                          <Typography variant="caption" color="text.secondary">Deutsch</Typography>
                          <Typography variant="body2">{pickMessage(row, "de") || "-"}</Typography>
                        </Box>
                        <Box sx={{ p: 1.5, bgcolor: "#fafafa", borderRadius: 1, direction: "rtl" }}>
                          <Typography variant="caption" color="text.secondary">العربية</Typography>
                          <Typography variant="body2">{pickMessage(row, "ar") || "-"}</Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Collapse>
                </Card>
              );
            })}
        </Stack>
        )}

        {activeTab === 0 && totalPages > 1 && (
          <Stack alignItems="center" mt={3}>
            <Pagination
              count={totalPages}
              page={uiPage}
              onChange={(_, v) => setUiPage(v)}
              size="small"
            />
          </Stack>
        )}
      </Box>

      <Dialog open={reportDialog.open} onClose={() => setReportDialog({ open: false, loading: false, row: null, data: null, error: "" })} fullWidth maxWidth="lg">
        <DialogTitle>Detailed placement report preview</DialogTitle>
        <DialogContent dividers>
          {reportDialog.loading && <Stack alignItems="center" sx={{ py: 6 }}><Typography>Building report...</Typography></Stack>}
          {reportDialog.error && <Typography color="error">{reportDialog.error}</Typography>}
          {reportDialog.data && (
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="h5" fontWeight={800}>{reportDialog.data.studentName || "Student"}</Typography>
                <Typography color="text.secondary">{reportDialog.data.email} · Session #{reportDialog.data.sessionId}</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                  <Chip label={`Target ${reportDialog.data.targetLevel || "-"}`} />
                  <Chip label={`Final ${reportDialog.data.finalLevel || "-"}`} color="success" />
                  <Chip label={`Score ${reportDialog.data.rawScore ?? 0}/${reportDialog.data.maximumScore ?? 0}`} color="warning" />
                  <Chip label={`Normalized ${reportDialog.data.normalizedScore ?? 0}/100`} />
                </Stack>
              </Box>
              {!reportDialog.data.historicalSnapshotComplete && <Alert severity="info">This older submission was reconstructed from the current question bank. New submissions preserve an exact historical question snapshot.</Alert>}
              {(reportDialog.data.sections || []).map((section) => (
                <Paper key={section.key} variant="outlined" sx={{ p: 2 }}>
                  <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={1} sx={{ mb: 1.5 }}>
                    <Typography variant="h6" fontWeight={800}>{section.key}</Typography>
                    <Typography fontWeight={700}>{section.earnedPoints}/{section.maximumPoints} points · {section.correctAnswers} correct · {section.incorrectAnswers} incorrect · {section.unansweredAnswers} unanswered</Typography>
                  </Stack>
                  <Stack spacing={1}>
                    {(section.questions || []).map((question) => {
                      const correct = question.state === "CORRECT";
                      const incorrect = question.state === "INCORRECT";
                      return (
                        <Box key={question.questionId} sx={{ p: 1.5, borderRadius: 1.5, borderLeft: "5px solid", borderColor: correct ? "success.main" : incorrect ? "error.main" : "grey.500", bgcolor: correct ? "#edf9f0" : incorrect ? "#fff1f2" : "#f5f5f5" }}>
                          <Typography fontWeight={800}>Question {question.questionNumber}: {question.questionText}</Typography>
                          <Typography variant="body2" sx={{ color: correct ? "success.dark" : incorrect ? "error.dark" : "text.secondary", fontWeight: 700 }}>Student answer: {question.studentAnswer || "No answer"}</Typography>
                          {!correct && <Typography variant="body2">Correct answer: {question.correctAnswer || "-"}</Typography>}
                          <Typography variant="caption">{question.earnedPoints}/{question.maximumPoints} points · {question.state}</Typography>
                        </Box>
                      );
                    })}
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportDialog({ open: false, loading: false, row: null, data: null, error: "" })}>Close</Button>
          {reportDialog.data && <Button variant="contained" startIcon={<PictureAsPdfIcon />} onClick={() => downloadDetailedReport(reportDialog.row)} disabled={!!reportAction}>{reportAction.startsWith("pdf-") ? "Generating..." : "Download PDF"}</Button>}
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmRetake.open} onClose={handleCancelRetake}>
        <DialogTitle>Enable Retake</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This will delete the student's current answers, session, evaluation, and cheating state according to the existing retake policy.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelRetake}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmRetake}
            disabled={
              !!(
                confirmRetake.row &&
                enablingRetake[confirmRetake.row.sessionId ?? confirmRetake.row.id]
              )
            }
          >
            Enable Retake
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!messageDialog.open} onClose={closeMessageDialog} fullWidth maxWidth="sm">
        <DialogTitle>Message student</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Subject"
              value={messageDialog.subject}
              onChange={(e) =>
                setMessageDialog((prev) => ({ ...prev, subject: e.target.value }))
              }
              fullWidth
            />
            <TextField
              label="Message"
              value={messageDialog.message}
              onChange={(e) =>
                setMessageDialog((prev) => ({ ...prev, message: e.target.value }))
              }
              multiline
              minRows={6}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeMessageDialog}>Cancel</Button>
          <Button variant="contained" onClick={submitMessage}>
            Send
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!blacklistDialog.open}
        onClose={() => setBlacklistDialog({ open: false, row: null, reason: "Manual teacher decision" })}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Blacklist student</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              This blocks the student from starting or continuing placement tests.
            </Typography>
            <TextField
              label="Reason"
              value={blacklistDialog.reason}
              onChange={(e) =>
                setBlacklistDialog((prev) => ({ ...prev, reason: e.target.value }))
              }
              fullWidth
              multiline
              minRows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlacklistDialog({ open: false, row: null, reason: "Manual teacher decision" })}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={blacklistStudent}>
            Blacklist
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
